import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "../../components/Button";
import { api } from "../../api/client";
import type { CompanyContact, CompanyContactDraft } from "../../api/types";

// 一間公司的窗口清單 —— **客戶那一列展開來的第二層。**
//
// 業主 2026-09-18:「一間公司 可能會有很多窗口 負責的案件不同
//                需要在客戶公司頁內 做一個二級選單 新增負責人」
//
// 公司本身那一組 contact_* 欄位留著沒動(報價單列印還在用它),
// 這裡是加上來的第二層:年會找誰、攤位找誰,分開記。
//
// **窗口可以真的刪**,跟客戶不一樣 —— 它沒有掛在任何歷史單據上。

const EMPTY: CompanyContactDraft = {
  name: "",
  title: "",
  duty: "",
  mobile: "",
  phone: "",
  email: "",
  note: "",
};

/** 欄位:[鍵, 標籤, 佔幾格]。順序就是打字的順序。 */
const FIELDS: [keyof CompanyContactDraft, string, string][] = [
  ["name", "姓名", "sm:col-span-2"],
  ["title", "職稱", "sm:col-span-2"],
  ["duty", "負責的案件", "sm:col-span-2"],
  ["mobile", "手機", "sm:col-span-2"],
  ["phone", "市內電話", "sm:col-span-2"],
  ["email", "Email", "sm:col-span-2"],
  ["note", "備註", "sm:col-span-6"],
];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-xs text-[var(--text-2)]">{label}</span>
      {children}
    </label>
  );
}

const INPUT =
  "h-9 w-full min-w-0 rounded-[var(--radius)] border border-[var(--border-strong)] " +
  "bg-[var(--surface)] px-2 text-sm text-[var(--text)]";

function ContactForm({
  value,
  onChange,
  onSave,
  onCancel,
  saving,
  submitLabel,
}: {
  value: CompanyContactDraft;
  onChange: (next: CompanyContactDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  submitLabel: string;
}) {
  return (
    <form
      className="grid grid-cols-1 gap-2 sm:grid-cols-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      {FIELDS.map(([key, label, span]) => (
        <div key={key} className={span}>
          <Row label={label}>
            <input
              className={INPUT}
              value={value[key]}
              aria-label={label}
              // 只有姓名是必填 —— 其他的當下不一定問得到,
              // 卡在必填會讓人乾脆不記。
              required={key === "name"}
              onChange={(event) => onChange({ ...value, [key]: event.target.value })}
            />
          </Row>
        </div>
      ))}
      <div className="flex items-center gap-2 sm:col-span-6">
        <Button type="submit" variant="primary" disabled={saving} icon={<Check size={15} />}>
          {saving ? "存檔中…" : submitLabel}
        </Button>
        <Button type="button" onClick={onCancel} icon={<X size={15} />}>
          取消
        </Button>
      </div>
    </form>
  );
}

export function CompanyContacts({ companyId }: { companyId: number }) {
  const queryClient = useQueryClient();
  const key = ["company-contacts", companyId];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<CompanyContactDraft>(EMPTY);
  // 正在編輯哪一筆(null = 沒有在編輯)。
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<CompanyContactDraft>(EMPTY);

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<CompanyContact[]>(`/companies/${companyId}/contacts`),
  });
  const contacts = Array.isArray(data) ? data : [];

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    setAdding(false);
    setEditingId(null);
    setDraft(EMPTY);
  };

  const create = useMutation({
    mutationFn: (body: CompanyContactDraft) =>
      api.post<CompanyContact>(`/companies/${companyId}/contacts`, body),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CompanyContactDraft }) =>
      api.patch<CompanyContact>(`/companies/contacts/${id}`, body),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/companies/contacts/${id}`),
    onSuccess: done,
  });

  const startEdit = (contact: CompanyContact) => {
    setEditingId(contact.id);
    setAdding(false);
    const { id: _id, company_id: _companyId, ...rest } = contact;
    setEditDraft(rest);
  };

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius)] bg-[var(--surface-2)] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold text-[var(--text)]">窗口、負責人</h3>
        {!adding ? (
          <Button
            type="button"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
              setDraft(EMPTY);
            }}
            icon={<Plus size={15} />}
          >
            新增窗口
          </Button>
        ) : null}
      </div>

      <p className="m-0 text-xs text-[var(--text-3)]">
        同一家公司不同案子常常是不同人在管。把負責的案件寫上去,下次要找人就不必問。
      </p>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          窗口載入失敗,請稍後再試。
        </p>
      ) : null}

      {adding ? (
        <div className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-3">
          <ContactForm
            value={draft}
            onChange={setDraft}
            onSave={() => create.mutate(draft)}
            onCancel={() => setAdding(false)}
            saving={create.isPending}
            submitLabel="存檔"
          />
          {create.isError ? (
            <p role="alert" className="m-0 mt-2 text-sm text-[var(--danger)]">
              存不進去,請稍後再試。
            </p>
          ) : null}
        </div>
      ) : null}

      {contacts.map((contact) =>
        editingId === contact.id ? (
          <div
            key={contact.id}
            className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-3"
          >
            <ContactForm
              value={editDraft}
              onChange={setEditDraft}
              onSave={() => update.mutate({ id: contact.id, body: editDraft })}
              onCancel={() => setEditingId(null)}
              saving={update.isPending}
              submitLabel="存檔"
            />
          </div>
        ) : (
          <div
            key={contact.id}
            className="flex flex-wrap items-start justify-between gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2"
          >
            <div className="min-w-0">
              <p className="m-0 text-sm font-medium text-[var(--text)]">
                {contact.name}
                {contact.title ? (
                  <span className="ml-2 text-xs font-normal text-[var(--text-2)]">
                    {contact.title}
                  </span>
                ) : null}
              </p>
              {contact.duty ? (
                <p className="m-0 text-xs text-[var(--text-2)]">負責:{contact.duty}</p>
              ) : null}
              <p className="tabular m-0 text-xs text-[var(--text-2)]">
                {[contact.mobile, contact.phone, contact.email].filter(Boolean).join("　")}
              </p>
              {contact.note ? (
                <p className="m-0 text-xs text-[var(--text-3)]">{contact.note}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button type="button" onClick={() => startEdit(contact)} icon={<Pencil size={15} />}>
                編輯
              </Button>
              {/* 窗口是可以真的刪的,所以還是問一句 —— 但不必像客戶那樣慎重,
                  它沒有掛在任何歷史單據上。 */}
              <Button
                type="button"
                onClick={() => {
                  if (window.confirm(
                    `確定要刪掉窗口「${contact.name}」嗎?\n\n` +
                    "會放進回收桶七天,期間隨時還原得回來。",
                  )) {
                    remove.mutate(contact.id);
                  }
                }}
                icon={<Trash2 size={15} />}
              >
                刪除
              </Button>
            </div>
          </div>
        ),
      )}

      {!isLoading && !error && contacts.length === 0 && !adding ? (
        <p className="m-0 text-sm text-[var(--text-3)]">
          這家公司還沒有記窗口。用上面的「新增窗口」加第一個。
        </p>
      ) : null}
    </div>
  );
}
